// 沙盘看板的图:折线(鼠标移上去有竖线和数值框)、堆叠面积(构成随时间怎么变)、环形(构成)、横条、分布、小曲线。
// 都画成 SVG 字符串,不引库。能看数的图把数据登记在 CHARTS 里,鼠标移动时由 hoverChart 画竖线和数值框。

export const esc = (s: unknown) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
/** 点了就办(不再弹确认框):原来确认框里的话变成按钮上的一行后果——问句后面有后果的只留后果,没有的把问句改成陈述。 */
export const cq = (s: string) => {
  const i = s.search(/[??]/);
  const tail = i >= 0 ? s.slice(i + 1).trim() : '';
  return (tail || (i >= 0 ? s.slice(0, i) : s)).replace(/[。;;,,]+$/, '');
};
/** 占比(0 到 1)写成百分数:1% 以上一位小数(整的不带),不到 1% 的按两位有效数字(0.04%、0.0021%),不会取整成 0%。 */
export const pctShare = (v: number) => {
  const p = v * 100;
  if (p === 0) return '0%';
  if (Math.abs(p) >= 1) return `${Number(p.toFixed(1))}%`;
  // 太小的(散户几股占一家大公司)写「不到 0.0001%」,免得写成 1e-7 这种
  return Math.abs(p) < 1e-4 ? '不到 0.0001%' : `${Number(p.toPrecision(2))}%`;
};
/** 按钮上的后果那一行(没有就是空)。 */
export const cqHTML = (s: string) => s ? `<small class="sb-cq">${esc(cq(s))}</small>` : '';

/** 钱:过万写万、过亿写亿。 */
export const money = (v: number) => {
  if (!Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1e8) return `${(v / 1e8).toFixed(2)} 亿`;
  if (a >= 1e4) return `${(v / 1e4).toFixed(a >= 1e6 ? 0 : 1)} 万`;
  return `${Math.round(v)} 元`;
};
/** 普通的数:大了缩写。 */
export const num = (v: number, d = 0) => {
  if (!Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1e8) return `${(v / 1e8).toFixed(2)} 亿`;
  if (a >= 1e4) return `${(v / 1e4).toFixed(1)} 万`;
  return v.toFixed(d);
};
export const pct = (v: number, d = 0) => `${(v * 100).toFixed(d)}%`;
export const dateOf = (d: number) => `第 ${Math.floor(d / 360) + 1} 年 ${Math.floor((d % 360) / 30) + 1} 月 ${(d % 30) + 1} 日`;
export const monthOf = (d: number) => `第 ${Math.floor(d / 360) + 1} 年 ${Math.floor((d % 360) / 30) + 1} 月`;
export const yearOf = (d: number) => `第 ${Math.floor(d / 360) + 1} 年`;
/** 装修几成新(worn 是旧了几成,0 新 1 旧完)。 */
export const newness = (worn: number) => {
  const n = Math.round((1 - worn) * 10);
  return n >= 10 ? '全新' : n <= 0 ? '旧完了' : `${'一二三四五六七八九'[n - 1]}成新`;
};

/**
 * 线和块的颜色:八个,同一套暖色里挑(11-美术.md),按固定的顺序给(砖红、钢蓝、赭黄、紫、绿、玫红、橙、青),不轮着用;
 * 第九个起一律是「其他」的灰。颜色是 sandbox.css 里的 --s1 到 --s8(深色模式是同样的色相调亮的一组),
 * 按 dataviz 的校验脚本验过:亮度带、饱和度、色弱看得出、正常眼睛分得开,浅色和深色都过。
 * 地图是画在 canvas 上的,用不了 CSS 变量,用 PALETTE 的十六进制(浅色那组)。
 */
export const PALETTE = ['#c0502e', '#2f72a8', '#d19a1c', '#7d4ea6', '#3d8b4a', '#b64a7c', '#e07a2c', '#00928a'];
export const SERIES = ['var(--s1)', 'var(--s2)', 'var(--s3)', 'var(--s4)', 'var(--s5)', 'var(--s6)', 'var(--s7)', 'var(--s8)'];
/** 第 k 个系列的颜色(从 0 数);第九个起是「其他」的灰。 */
export const seriesColor = (k: number) => SERIES[k] ?? 'var(--s-other)';

export interface Line {
  name: string;
  vals: number[];
  /** 原始分币等精确显示文本；坐标仍由vals计算。 */
  texts?: string[];
  color?: string;
  /** 可选虚线；未指定时保留原有实线和图例。 */
  dash?: readonly [number, number];
  fmt?: (v: number) => string;
}

/** 图例和选择标签共用线型示意；不把第九条起的股东合成“其他”。 */
export function lineSwatch(color: string, dash?: readonly [number, number]): string {
  const background = dash ? `repeating-linear-gradient(to right,${color} 0 ${dash[0]}px,transparent ${dash[0]}px ${dash[0] + dash[1]}px)` : color;
  // 不插入额外SVG：现有hover/range以图容器的第一份SVG为坐标系。
  return `<span aria-hidden="true" style="display:inline-block;vertical-align:middle;width:18px;height:2px;background:${background}"></span>`;
}

interface Registered {
  xs: number[];
  lines: Line[];
  fmt: (v: number) => string;
  L: number;
  R: number;
  W: number;
  label: (x: number) => string;
  /** 浮框里线下面另加的几行(那个点上别的数:合计、比率……) */
  rows?: (i: number) => [string, string][];
  /** 折线的纵向位置(浮框里的点按它画;线中间有空的,点也对得上),画图区的上下边 */
  ys?: (v: number) => number;
  top?: number;
  bottom?: number;
}
/** 浮框另加几行:第 i 个点上别的数。 */
export type TipRows = (i: number) => [string, string][];
/** 能看数的图:id → 数据。每次重画清一次。 */
export const CHARTS = new Map<string, Registered>();
let seq = 0;
export const resetCharts = () => {
  CHARTS.clear();
  seq = 0;
};

/**
 * 折线图。xs 是游戏日;log 为真按对数画(涨跌几十倍也看得清),zero 为真纵轴从 0 起。
 * key 是这张图的名字(刷新以后鼠标停的位置还在同一张图上)。
 */
export function lineChart(xs: number[], lines: Line[], opt: { h?: number; fmt?: (v: number) => string; log?: boolean; zero?: boolean; key?: string; area?: boolean; label?: (x: number) => string; rows?: TipRows; range?: boolean; annualDelta?: boolean } = {}): string {
  const W = 560;
  const H = opt.h ?? 160;
  const L = 56, R = 10, T = 10, B = 22;
  const fmt = opt.fmt ?? ((v: number) => num(v, Math.abs(v) < 10 ? 1 : 0));
  if (xs.length < 2) return `<div class="sb-empty">数据还不够两个月</div>`;
  let lo = Infinity, hi = -Infinity;
  for (const l of lines) for (const v of l.vals) if (Number.isFinite(v)) { lo = Math.min(lo, v); hi = Math.max(hi, v); }
  if (!Number.isFinite(lo)) return `<div class="sb-empty">没有</div>`;
  const log = !!opt.log && lo > 0;
  if (opt.zero && !log) lo = Math.min(0, lo);
  if (hi === lo) hi = lo + (Math.abs(lo) || 1);
  // 刻度取整:线性的按 1、2、2.5、5 的整数倍,对数的按 1、2、5 × 10 的几次方
  let ticks: number[];
  if (log) {
    ticks = logTicks(lo, hi);
  } else {
    const st = niceStep(hi - lo, 4);
    lo = Math.floor(lo / st + 1e-9) * st;
    hi = Math.ceil(hi / st - 1e-9) * st;
    if (hi <= lo) hi = lo + st;
    ticks = [];
    for (let v = lo; v <= hi + st * 1e-6; v += st) ticks.push(Math.abs(v) < st * 1e-9 ? 0 : v);
  }
  const tf = (v: number) => (log ? Math.log(Math.max(v, 1e-9)) : v);
  const [a, b] = [tf(lo), tf(hi)];
  const x = (i: number) => L + ((W - L - R) * i) / (xs.length - 1);
  const y = (v: number) => T + (H - T - B) * (1 - (tf(v) - a) / (b - a || 1));
  const id = opt.key ?? `c${++seq}`;
  const gid = `ga-${id.replace(/[^\w-]/g, '_')}`;
  let s = `<svg class="sb-chart" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">`;
  ticks.forEach((v, k) => {
    const yy = y(v);
    s += `<line x1="${L}" x2="${W - R}" y1="${yy.toFixed(1)}" y2="${yy.toFixed(1)}" class="${k === 0 || v === 0 ? 'g0' : 'g'}"/><text x="${L - 6}" y="${(yy + 3).toFixed(1)}" text-anchor="end">${esc(fmt(v))}</text>`;
  });
  const years = Math.max(1, Math.round((xs[xs.length - 1] - xs[0]) / 360));
  const every = years > 30 ? 10 : years > 12 ? 5 : years > 5 ? 2 : 1;
  xs.forEach((d, i) => {
    const yr = Math.floor(d / 360) + 1;
    const prev = i > 0 ? Math.floor(xs[i - 1] / 360) + 1 : 0;
    if (yr !== prev && (yr - 1) % every === 0) s += `<line x1="${x(i)}" x2="${x(i)}" y1="${T}" y2="${H - B}" class="gy"/><text x="${x(i)}" y="${H - 7}" text-anchor="middle">${yr}年</text>`;
  });
  lines.forEach((l, k) => {
    const c = l.color ?? seriesColor(k);
    const pts = l.vals.map((v, i) => (Number.isFinite(v) ? `${x(i).toFixed(1)},${y(v).toFixed(1)}` : '')).filter(Boolean);
    if (opt.area && k === 0 && pts.length) {
      // 第一条线下面铺一层渐淡的颜色
      s += `<defs><linearGradient id="${gid}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style="stop-color:${c}" stop-opacity="0.26"/><stop offset="1" style="stop-color:${c}" stop-opacity="0.02"/></linearGradient></defs>`;
      s += `<polygon points="${x(0)},${y(Math.max(lo, 0))} ${pts.join(' ')} ${x(xs.length - 1)},${y(Math.max(lo, 0))}" fill="url(#${gid})"/>`;
    }
    // 未知值是断线，不把缺失两侧直接接成一段历史。
    const segments: string[][] = [[]];
    l.vals.forEach((v, i) => {
      if (Number.isFinite(v)) segments[segments.length - 1].push(`${x(i).toFixed(1)},${y(v).toFixed(1)}`);
      else if (segments[segments.length - 1].length) segments.push([]);
    });
    for (const segment of segments.filter(p => p.length)) {
      s += `<polyline points="${segment.join(' ')}" fill="none" style="stroke:${c}" stroke-width="1.8"${l.dash ? ` stroke-dasharray="${esc(l.dash.join(' '))}"` : ''} stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke"/>`;
      if (segment.length === 1) { const [cx, cy] = segment[0].split(','); s += `<circle cx="${cx}" cy="${cy}" r="2.4" style="fill:${c}"/>`; }
    }
  });
  // 每条线最后一个点:一个小圆点(现在在哪)
  lines.forEach((l, k) => {
    const n = l.vals.length;
    if (n && Number.isFinite(l.vals[n - 1])) s += `<circle cx="${x(n - 1).toFixed(1)}" cy="${y(l.vals[n - 1]).toFixed(1)}" r="2.4" style="fill:${l.color ?? seriesColor(k)}" class="end"/>`;
  });
  s += `${opt.range ? '<g class="rsel"></g>' : ''}<g class="hov"></g></svg>`;
  CHARTS.set(id, { xs, lines, fmt, L, R, W, label: opt.label ?? monthOf, rows: opt.rows, ys: y, top: T, bottom: H - B });
  const legend = `<div class="sb-legend">${lines.map((l, k) => `<span>${l.dash ? lineSwatch(l.color ?? seriesColor(k), l.dash) : `<i style="background:${l.color ?? seriesColor(k)}"></i>`}${esc(l.name)} <b>${esc(l.texts?.[l.vals.length - 1] ?? (l.fmt ?? fmt)(l.vals[l.vals.length - 1]))}</b>${opt.annualDelta === false ? '' : delta(l.vals)}</span>`).join('')}${opt.range ? '<small class="sb-legend-tip">长按拖选一段看区间</small>' : ''}</div>`;
  return `<div class="sb-cw" data-chart="${esc(id)}"${opt.range ? ' data-range="1"' : ''}>${legend}${s}<div class="sb-tip" hidden></div>${opt.range ? '<div class="sb-tip sb-rtip" hidden></div>' : ''}</div>`;
}

/** 刻度的间隔:把 span 分成大约 n 格,取 1、2、2.5、5 乘 10 的几次方里最近的。 */
export function niceStep(span: number, n: number): number {
  if (!(span > 0)) return 1;
  const raw = span / Math.max(1, n);
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const m = raw / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p;
}

/** 对数刻度:lo 到 hi 之间 1、2、5 × 10 的几次方;太多就只留 10 的几次方,太少就均匀取五个。 */
function logTicks(lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let e = Math.floor(Math.log10(lo)); e <= Math.ceil(Math.log10(hi)); e++) {
    for (const m of [1, 2, 5]) {
      const v = m * Math.pow(10, e);
      if (v >= lo * 0.999 && v <= hi * 1.001) out.push(v);
    }
  }
  if (out.length > 6) {
    const tens = out.filter((v) => Math.abs(Math.log10(v) - Math.round(Math.log10(v))) < 1e-9);
    if (tens.length >= 2) return tens;
  }
  if (out.length >= 2) return out;
  return [0, 1, 2, 3, 4].map((k) => Math.exp(Math.log(lo) + ((Math.log(hi) - Math.log(lo)) * k) / 4));
}

/** 最后一个数比一年前(十二个点以前)涨跌多少。 */
function delta(vals: number[]): string {
  const n = vals.length;
  if (n < 13) return '';
  const a = vals[n - 13], b = vals[n - 1];
  if (!Number.isFinite(a) || !Number.isFinite(b) || Math.abs(a) < 1e-9) return '';
  const d = b / a - 1;
  if (Math.abs(d) < 0.005) return '';
  return ` <em class="${d > 0 ? 'up' : 'down'}">${d > 0 ? '▲' : '▼'}${Math.abs(d * 100).toFixed(0)}%</em>`;
}

/** 鼠标在一张图上:画竖线、每条线上一个点,旁边一个数值框。返回停在第几个点(刷新以后接着画)。 */
export function hoverChart(el: HTMLElement, clientX: number | null): number | null {
  const id = el.dataset.chart!;
  const c = CHARTS.get(id);
  const svg = el.querySelector('svg') as SVGSVGElement | null;
  const g = svg?.querySelector('.hov') as SVGGElement | null;
  const tip = el.querySelector('.sb-tip') as HTMLElement | null;
  if (!c || !svg || !g || !tip) return null;
  if (clientX == null) {
    g.innerHTML = '';
    tip.hidden = true;
    return null;
  }
  const r = svg.getBoundingClientRect();
  const vx = ((clientX - r.left) / r.width) * c.W;
  const n = c.xs.length;
  const i = Math.max(0, Math.min(n - 1, Math.round(((vx - c.L) / (c.W - c.L - c.R)) * (n - 1))));
  return drawHover(el, i);
}

/** 按点的下标画(刷新以后恢复用)。 */
export function drawHover(el: HTMLElement, i: number): number | null {
  const c = CHARTS.get(el.dataset.chart!);
  const svg = el.querySelector('svg') as SVGSVGElement | null;
  const g = svg?.querySelector('.hov') as SVGGElement | null;
  const tip = el.querySelector('.sb-tip') as HTMLElement | null;
  if (!c || !svg || !g || !tip || i >= c.xs.length) return null;
  const vb = svg.viewBox.baseVal;
  const n = c.xs.length;
  const px = c.L + ((c.W - c.L - c.R) * i) / (n - 1);
  // 纵向位置:折线图按登记的纵向比例算(线中间有空的也对得上);堆叠图从已画的线上取同一个点
  let dots = '';
  if (c.ys) {
    c.lines.forEach((l, k) => {
      const v = l.vals[i];
      if (Number.isFinite(v)) dots += `<circle cx="${px.toFixed(1)}" cy="${c.ys!(v).toFixed(1)}" r="3.2" style="fill:${l.color ?? seriesColor(k)};stroke:var(--card)" stroke-width="1.2" vector-effect="non-scaling-stroke"/>`;
    });
  } else {
    const polys = [...svg.querySelectorAll('polyline')];
    polys.forEach((pl, k) => {
      const pts = (pl.getAttribute('points') ?? '').split(' ');
      const p = pts[i]?.split(',');
      if (p) dots += `<circle cx="${p[0]}" cy="${p[1]}" r="3.2" style="fill:${c.lines[k]?.color ?? seriesColor(k)};stroke:var(--card)" stroke-width="1.2" vector-effect="non-scaling-stroke"/>`;
    });
  }
  g.innerHTML = `<line x1="${px}" x2="${px}" y1="4" y2="${vb.height - 18}" class="cross"/>${dots}`;
  tip.innerHTML = `<b>${esc(c.label(c.xs[i]))}</b>` + c.lines.map((l, k) => `<div><i style="background:${l.color ?? seriesColor(k)}"></i>${esc(l.name)}<span>${esc(l.texts?.[i] ?? (l.fmt ?? c.fmt)(l.vals[i]))}</span></div>`).join('')
    + (c.rows?.(i) ?? []).map(([k, v]) => `<div class="more">${esc(k)}<span>${esc(v)}</span></div>`).join('');
  tip.hidden = false;
  const frac = px / vb.width;
  tip.style.left = frac > 0.6 ? '' : `calc(${(frac * 100).toFixed(1)}% + 10px)`;
  tip.style.right = frac > 0.6 ? `calc(${((1 - frac) * 100).toFixed(1)}% + 10px)` : '';
  return i;
}

/** 长按拖选的一段:哪张图、从第几个点到第几个点(刷新以后照原样画回去,点别处收起)。 */
let rangeSel: { id: string; a: number; b: number } | null = null;

/** 图上 clientX 处是第几个点。 */
const pointAt = (el: HTMLElement, clientX: number): number | null => {
  const c = CHARTS.get(el.dataset.chart ?? '');
  const svg = el.querySelector('svg');
  if (!c || !svg) return null;
  const r = svg.getBoundingClientRect();
  const vx = ((clientX - r.left) / r.width) * c.W;
  const n = c.xs.length;
  return Math.max(0, Math.min(n - 1, Math.round(((vx - c.L) / (c.W - c.L - c.R)) * (n - 1))));
};

/** 画选的一段:底色、浮框(起止、几个月,每条线起点 → 终点、变了多少)。 */
export function drawRange(el: HTMLElement, a: number, b: number): void {
  const c = CHARTS.get(el.dataset.chart ?? '');
  const g = el.querySelector('svg .rsel');
  const tip = el.querySelector('.sb-rtip') as HTMLElement | null;
  if (!c || !g || !tip) return;
  const n = c.xs.length;
  const i0 = Math.max(0, Math.min(a, b, n - 1)), i1 = Math.min(n - 1, Math.max(a, b));
  const x = (i: number) => c.L + ((c.W - c.L - c.R) * i) / (n - 1);
  const x0 = x(i0) - (i0 === i1 ? 3 : 0), x1 = x(i1) + (i0 === i1 ? 3 : 0);
  g.innerHTML = `<rect class="rs" x="${x0.toFixed(1)}" y="${c.top ?? 0}" width="${(x1 - x0).toFixed(1)}" height="${((c.bottom ?? 0) - (c.top ?? 0)).toFixed(1)}"/>`;
  const span = c.xs[i1] - c.xs[i0];
  const monthly = n > 1 && (c.xs[1] - c.xs[0]) % 30 === 0;
  const signed = (v: number, f: (v: number) => string) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${f(Math.abs(v))}`;
  tip.innerHTML = `<b>${esc(c.label(c.xs[i0]))} – ${esc(c.label(c.xs[i1]))}</b><em>${monthly ? `${Math.round(span / 30)} 个月` : `${span} 天`}</em>` + c.lines.map((l, k) => {
    const f = l.fmt ?? c.fmt, v0 = l.vals[i0], v1 = l.vals[i1];
    if (!Number.isFinite(v0) || !Number.isFinite(v1)) return `<div><i style="background:${l.color ?? seriesColor(k)}"></i>${esc(l.name)}<span>—</span></div>`;
    const d = v1 - v0;
    return `<div><i style="background:${l.color ?? seriesColor(k)}"></i>${esc(l.name)}<span>${esc(f(v0))} → ${esc(f(v1))}</span></div><div class="more">变了<span class="${d > 0 ? 'kup' : d < 0 ? 'kdown' : ''}">${esc(signed(d, f))}${Math.abs(v0) > 1e-9 ? ` · ${d > 0 ? '+' : ''}${((d / Math.abs(v0)) * 100).toFixed(1)}%` : ''}</span></div>`;
  }).join('');
  const fa = x0 / c.W, fb = x1 / c.W;
  tip.setAttribute('style', fb < 0.55 ? `left:calc(${(fb * 100).toFixed(1)}% + 10px)` : fa > 0.45 ? `right:calc(${((1 - fa) * 100).toFixed(1)}% + 10px)` : 'left:8px');
  tip.hidden = false;
}

const clearRange = () => {
  if (!rangeSel) return;
  const el = document.querySelector(`.sb-cw[data-chart="${CSS.escape(rangeSel.id)}"]`);
  rangeSel = null;
  if (!el) return;
  const g = el.querySelector('svg .rsel');
  if (g) g.innerHTML = '';
  const tip = el.querySelector('.sb-rtip') as HTMLElement | null;
  if (tip) tip.hidden = true;
};

/** 重画以后:选着的一段照原样画回去。 */
export function restoreChartRange(): void {
  if (!rangeSel) return;
  const el = document.querySelector(`.sb-cw[data-chart="${CSS.escape(rangeSel.id)}"]`) as HTMLElement | null;
  if (el) drawRange(el, rangeSel.a, rangeSel.b);
}

/** 装上长按拖选(带 range 的折线图;鼠标、手指一样:按住不动 0.3 秒再拖,松手留着,点别处收起)。整页只装一回。 */
export function installChartRange(): void {
  let g: { el: HTMLElement; id: number; x0: number; y0: number; timer: number; on: boolean } | null = null;
  document.addEventListener('pointerdown', (e) => {
    const el = (e.target as Element).closest?.('.sb-cw[data-range]') as HTMLElement | null;
    clearRange();
    if (!el || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const cur = { el, id: e.pointerId, x0: e.clientX, y0: e.clientY, timer: 0, on: false };
    cur.timer = window.setTimeout(() => {
      if (g !== cur) return;
      const i = pointAt(el, cur.x0);
      if (i == null) return;
      cur.on = true;
      rangeSel = { id: el.dataset.chart ?? '', a: i, b: i };
      drawRange(el, i, i);
    }, 300);
    g = cur;
  });
  document.addEventListener('pointermove', (e) => {
    if (!g || e.pointerId !== g.id) return;
    if (!g.on) {
      if (Math.hypot(e.clientX - g.x0, e.clientY - g.y0) > 6) { clearTimeout(g.timer); g = null; }
      return;
    }
    e.preventDefault();
    const i = pointAt(g.el.isConnected ? g.el : (document.querySelector(`.sb-cw[data-chart="${CSS.escape(rangeSel?.id ?? '')}"]`) as HTMLElement ?? g.el), e.clientX);
    if (i == null || !rangeSel || i === rangeSel.b) return;
    rangeSel.b = i;
    restoreChartRange();
  });
  const up = (e: PointerEvent) => { if (g && e.pointerId === g.id) { clearTimeout(g.timer); g = null; } };
  document.addEventListener('pointerup', up);
  document.addEventListener('pointercancel', up);
}

/** 堆叠面积:几样东西加起来的总量怎么随时间变、各自占多少。 */
export function areaChart(xs: number[], parts: Line[], opt: { h?: number; fmt?: (v: number) => string; key?: string; rows?: TipRows } = {}): string {
  const n = xs.length;
  if (n < 2 || !parts.length) return `<div class="sb-empty">数据还不够两个月</div>`;
  const W = 560, H = opt.h ?? 160, L = 56, R = 10, T = 10, B = 22;
  const fmt = opt.fmt ?? ((v: number) => num(v));
  const tops = xs.map((_, i) => parts.reduce((a, p) => a + Math.max(0, p.vals[i] ?? 0), 0));
  const top = Math.max(...tops, 1e-9);
  const st = niceStep(top, 4);
  const hi = Math.ceil(top / st - 1e-9) * st || top;
  const x = (i: number) => L + ((W - L - R) * i) / (n - 1);
  const y = (v: number) => T + (H - T - B) * (1 - v / hi);
  let s = `<svg class="sb-chart" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">`;
  for (let v = 0; v <= hi + st * 1e-6; v += st) {
    const yy = y(v);
    s += `<line x1="${L}" x2="${W - R}" y1="${yy.toFixed(1)}" y2="${yy.toFixed(1)}" class="${v === 0 ? 'g0' : 'g'}"/><text x="${L - 6}" y="${(yy + 3).toFixed(1)}" text-anchor="end">${esc(fmt(v))}</text>`;
  }
  const years = Math.max(1, Math.round((xs[n - 1] - xs[0]) / 360));
  const every = years > 30 ? 10 : years > 12 ? 5 : years > 5 ? 2 : 1;
  xs.forEach((d, i) => {
    const yr = Math.floor(d / 360) + 1;
    const prev = i > 0 ? Math.floor(xs[i - 1] / 360) + 1 : 0;
    if (yr !== prev && (yr - 1) % every === 0) s += `<text x="${x(i)}" y="${H - 7}" text-anchor="middle">${yr}年</text>`;
  });
  const base = xs.map(() => 0);
  const hidden: string[] = [];
  parts.forEach((p, k) => {
    const c = p.color ?? seriesColor(k);
    const top = xs.map((_, i) => base[i] + Math.max(0, p.vals[i] ?? 0));
    const up = top.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    const down = base.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).reverse();
    s += `<polygon points="${[...up, ...down].join(' ')}" style="fill:${c}" opacity="0.82" class="lay"/>`;
    hidden.push(`<polyline points="${up.join(' ')}" fill="none" stroke="none"/>`);
    top.forEach((v, i) => (base[i] = v));
  });
  s += hidden.join('') + `<g class="hov"></g></svg>`;
  const id = opt.key ?? `c${++seq}`;
  CHARTS.set(id, { xs, lines: parts, fmt, L, R, W, label: monthOf, rows: opt.rows });
  const legend = `<div class="sb-legend">${parts.map((p, k) => `<span><i style="background:${p.color ?? seriesColor(k)}"></i>${esc(p.name)} <b>${esc(fmt(p.vals[n - 1]))}</b></span>`).join('')}</div>`;
  return `<div class="sb-cw" data-chart="${esc(id)}">${legend}${s}<div class="sb-tip" hidden></div></div>`;
}

/** 环形:构成(中间写合计)。 */
export function donut(items: { label: string; value: number; color?: string; link?: string }[], fmt: (v: number) => string = (v) => num(v), center = '合计'): string {
  const pos = items.filter((i) => i.value > 0);
  const total = pos.reduce((a, i) => a + i.value, 0);
  if (total <= 0) return `<div class="sb-empty">没有</div>`;
  const R = 52, r = 34, C = 60;
  let ang = -Math.PI / 2;
  let s = `<svg class="sb-donut" viewBox="0 0 120 120">`;
  pos.forEach((it, k) => {
    const a = (it.value / total) * Math.PI * 2;
    const a2 = ang + Math.min(a, Math.PI * 2 - 1e-4);
    const large = a > Math.PI ? 1 : 0;
    const p = (rr: number, t: number) => `${(C + rr * Math.cos(t)).toFixed(2)},${(C + rr * Math.sin(t)).toFixed(2)}`;
    s += `<path class="seg" d="M${p(R, ang)} A${R},${R} 0 ${large} 1 ${p(R, a2)} L${p(r, a2)} A${r},${r} 0 ${large} 0 ${p(r, ang)} Z" style="fill:${it.color ?? seriesColor(k)}"><title>${esc(`${it.label}:${fmt(it.value)}(${pct(it.value / total, 1)})`)}</title></path>`;
    ang = a2;
  });
  s += `<text x="60" y="56" text-anchor="middle" class="c1">${esc(center)}</text><text x="60" y="72" text-anchor="middle" class="c2">${esc(fmt(total))}</text></svg>`;
  const key = pos
    .map((it, k) => `<div class="sb-key"${it.link ?? ''}><i style="background:${it.color ?? seriesColor(k)}"></i><span>${esc(it.label)}</span><b>${esc(fmt(it.value))}</b><em>${pct(it.value / total, 1)}</em></div>`)
    .join('');
  return `<div class="sb-donutwrap">${s}<div class="sb-keys">${key}</div></div>`;
}

/** 横条:每一项一行,条的长短按最大的一项。 */
export function bars(items: { label: string; value: number; color?: string; link?: string; sub?: string }[], fmt: (v: number) => string = (v) => num(v)): string {
  if (!items.length) return `<div class="sb-empty">没有</div>`;
  const max = Math.max(...items.map((i) => Math.abs(i.value)), 1e-9);
  return `<div class="sb-bars">${items
    .map(
      (i) =>
        `<div class="sb-bar"${i.link ?? ''}><span class="l">${esc(i.label)}</span><span class="t"><i style="width:${((Math.abs(i.value) / max) * 100).toFixed(1)}%;background:${i.color ?? 'var(--ink-3)'}"></i></span><b>${esc(fmt(i.value))}</b>${i.sub ? `<em>${esc(i.sub)}</em>` : ''}</div>`,
    )
    .join('')}</div>`;
}

/** 分布:把一串数分成若干档,画柱子(身家、工资、房价……)。 */
export function histogram(vals: number[], bins = 12, fmt: (v: number) => string = (v) => num(v), log = false): string {
  const v = vals.filter((x) => Number.isFinite(x) && (!log || x > 0));
  if (v.length < 3) return `<div class="sb-empty">太少</div>`;
  const tf = (x: number) => (log ? Math.log(x) : x);
  const inv = (x: number) => (log ? Math.exp(x) : x);
  const lo = Math.min(...v.map(tf)), hi = Math.max(...v.map(tf));
  const w = (hi - lo) / bins || 1;
  const counts = new Array(bins).fill(0);
  v.forEach((x) => counts[Math.min(bins - 1, Math.floor((tf(x) - lo) / w))]++);
  const max = Math.max(...counts);
  const W = 560, H = 120, B = 20;
  const bw = W / bins;
  let s = `<svg class="sb-chart" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">`;
  counts.forEach((c, i) => {
    const h = ((H - B - 6) * c) / max;
    s += `<rect x="${i * bw + 2}" y="${H - B - h}" width="${bw - 4}" height="${h}" rx="2" style="fill:var(--s2)" opacity="0.75"><title>${esc(`${fmt(inv(lo + i * w))} 到 ${fmt(inv(lo + (i + 1) * w))}:${c} 个`)}</title></rect>`;
    if (i % 3 === 0) s += `<text x="${i * bw + 2}" y="${H - 6}">${esc(fmt(inv(lo + i * w)))}</text>`;
  });
  return s + `</svg>`;
}

/** 小曲线(顶栏、表格里用)。 */
export function spark(vals: number[], color = 'var(--ink-3)', w = 60, h = 16): string {
  const v = vals.filter((x) => Number.isFinite(x));
  if (v.length < 2) return '';
  const lo = Math.min(...v), hi = Math.max(...v);
  const X = (i: number) => 1 + (i / (v.length - 1)) * (w - 3);
  const Y = (x: number) => h - 1.5 - ((x - lo) / (hi - lo || 1)) * (h - 3);
  const pts = v.map((x, i) => `${X(i).toFixed(1)},${Y(x).toFixed(1)}`).join(' ');
  const n = v.length - 1;
  return `<svg class="sb-spark" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}"><polyline points="${pts}" fill="none" style="stroke:${color}" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round"/><circle cx="${X(n).toFixed(1)}" cy="${Y(v[n]).toFixed(1)}" r="1.5" style="fill:${color}"/></svg>`;
}

/** 小数字块:标题、数、下面一行小字。 */
export function tile(label: string, value: string, sub = '', cls = '', link = ''): string {
  return `<div class="sb-tile ${cls}"${link}><span>${esc(label)}</span><b>${value}</b>${sub ? `<em>${sub}</em>` : ''}</div>`;
}

/** 表格:列头、行;cells 已经是 HTML。sortKey 给了的话,列头可以点来排序(data-sort="key:列号")。 */
export function table(head: string[], rows: string[][], opt: { cls?: string; sortKey?: string; sortCol?: number; sortDir?: number } = {}): string {
  if (!rows.length) return `<div class="sb-empty">没有</div>`;
  // 数字列靠右对齐(个位对个位):一列里大多数格子是数(钱、人数、百分比)就算数字列
  const plain = (c: string) => String(c ?? '').replace(/<[^>]*>/g, '').replace(/&[a-z]+;/g, '').trim();
  const isNum = head.map((_, i) => {
    let n = 0, all = 0;
    for (const r of rows) {
      const t = plain(r[i]);
      if (!t || t === '—') continue;
      all++;
      if (/^[+\-−]?\s?\d/.test(t)) n++;
    }
    return all > 0 && n >= all * 0.6;
  });
  const th = head
    .map((h, i) => {
      const cls = isNum[i] ? 'n' : '';
      if (!opt.sortKey) return `<th${cls ? ` class="${cls}"` : ''}>${esc(h)}</th>`;
      const on = opt.sortCol === i;
      return `<th class="sortable${on ? ' on' : ''} ${cls}" data-sort="${esc(opt.sortKey)}:${i}">${esc(h)}${on ? `<i class="dir">${opt.sortDir! > 0 ? '▲' : '▼'}</i>` : ''}</th>`;
    })
    .join('');
  return `<div class="sb-tablewrap"><table class="sb-table ${opt.cls ?? ''}"><thead><tr>${th}</tr></thead><tbody>${rows.map((r) => `<tr>${r.map((c, i) => `<td${isNum[i] ? ' class="n"' : ''}>${c}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`;
}

/** 一年来涨跌多少(和十二个点以前比),给数字卡的小字用;worse 为真的涨了算坏事(标红)。不满一年的空着。 */
export function trendNote(vals: number[], worse = false): string {
  const n = vals.length;
  if (n < 13) return '';
  const a = vals[n - 13], b = vals[n - 1];
  if (!Number.isFinite(a) || !Number.isFinite(b) || Math.abs(a) < 1e-9) return '';
  const d = b / a - 1;
  if (Math.abs(d) < 0.005) return '<span class="flat">和一年前差不多</span>';
  const good = (d > 0) !== worse;
  return `<span class="${good ? 'up' : 'down'}">${d > 0 ? '▲' : '▼'}${Math.abs(d * 100).toFixed(0)}%</span> 一年`;
}

/**
 * 瀑布图:从头一项起一项项加减,最后一行是合计(和各项加起来一样)。正的绿、负的红,合计墨色;
 * 每一行的条从上一行的累计接着画,看得出哪一块最大。items 的 note 是小字。
 */
export function waterfall(items: { label: string; value: number; note?: string }[], fmt: (v: number) => string, total: string): string {
  const rows = items.filter((i) => Number.isFinite(i.value) && Math.abs(i.value) >= 0.5);
  if (!rows.length) return `<div class="sb-empty">没有</div>`;
  let run = 0;
  const spans = rows.map((r) => {
    const from = run;
    run += r.value;
    return { ...r, from, to: run };
  });
  const lo = Math.min(0, ...spans.map((s) => Math.min(s.from, s.to)));
  const hi = Math.max(0, ...spans.map((s) => Math.max(s.from, s.to)));
  const w = hi - lo || 1;
  const pos = (v: number) => ((v - lo) / w) * 100;
  const bar = (a: number, b: number, color: string) => `<i style="left:${pos(Math.min(a, b)).toFixed(2)}%;width:${Math.max(0.6, Math.abs(pos(b) - pos(a))).toFixed(2)}%;background:${color}"></i>`;
  const zero = `<u style="left:${pos(0).toFixed(2)}%"></u>`;
  const line = (label: string, note: string, cls: string, v: string, b: string) =>
    `<div class="wf-row ${cls}"><span class="l">${esc(label)}${note ? `<small>${esc(note)}</small>` : ''}</span><span class="t">${zero}${b}</span><b>${v}</b></div>`;
  return `<div class="wf">${spans.map((s) => line(s.label, s.note ?? '', s.value >= 0 ? 'pos' : 'neg', `${s.value >= 0 ? '+' : '−'}${esc(fmt(Math.abs(s.value)))}`, bar(s.from, s.to, s.value >= 0 ? 'var(--good)' : 'var(--bad)'))).join('')}` +
    line(total, '', 'sum', esc(fmt(run)), bar(0, run, 'var(--ink-2)')) + `</div>`;
}

/**
 * 方块图(按面积分):每一项一块,面积按数大小,颜色给定;link 是块上的点击属性(比如 data-kind)。
 * 用「尽量方」的切法(squarified):一排排往里放,每排的长宽比尽量接近 1。
 */
export function treemap(items: { label: string; value: number; color: string; link?: string; sub?: string }[], fmt: (v: number) => string, h = 220): string {
  const list = items.filter((i) => i.value > 0).sort((a, b) => b.value - a.value);
  const total = list.reduce((s, i) => s + i.value, 0);
  if (!list.length || total <= 0) return `<div class="sb-empty">没有</div>`;
  const W = 100, H = 100; // 按百分比摆
  type Box = { i: number; x: number; y: number; w: number; h: number };
  const boxes: Box[] = [];
  const areas = list.map((i) => (i.value / total) * W * H);
  let rect = { x: 0, y: 0, w: W, h: H };
  let k = 0;
  const worst = (row: number[], side: number) => {
    const s = row.reduce((a, b) => a + b, 0);
    const mx = Math.max(...row), mn = Math.min(...row);
    return Math.max((side * side * mx) / (s * s), (s * s) / (side * side * mn));
  };
  while (k < areas.length) {
    const side = Math.min(rect.w, rect.h);
    const row = [areas[k]];
    let j = k + 1;
    while (j < areas.length && worst([...row, areas[j]], side) <= worst(row, side)) {
      row.push(areas[j]);
      j++;
    }
    const s = row.reduce((a, b) => a + b, 0);
    if (rect.w >= rect.h) {
      // 竖着放一列(占掉左边一条)
      const cw = s / rect.h;
      let y = rect.y;
      row.forEach((a, n) => {
        const bh = a / cw;
        boxes.push({ i: k + n, x: rect.x, y, w: cw, h: bh });
        y += bh;
      });
      rect = { x: rect.x + cw, y: rect.y, w: rect.w - cw, h: rect.h };
    } else {
      // 横着放一排(占掉上边一条)
      const rh = s / rect.w;
      let x = rect.x;
      row.forEach((a, n) => {
        const bw = a / rh;
        boxes.push({ i: k + n, x, y: rect.y, w: bw, h: rh });
        x += bw;
      });
      rect = { x: rect.x, y: rect.y + rh, w: rect.w, h: rect.h - rh };
    }
    k = j;
  }
  return `<div class="tm" style="height:${h}px">${boxes
    .map((b) => {
      const it = list[b.i];
      const small = b.w * b.h < 180;
      return `<div class="tm-b"${it.link ?? ''} style="left:${b.x.toFixed(2)}%;top:${b.y.toFixed(2)}%;width:${b.w.toFixed(2)}%;height:${b.h.toFixed(2)}%;--c:${it.color}" title="${esc(`${it.label}:${fmt(it.value)}(${pct(it.value / total, 1)})`)}">${small ? '' : `<b>${esc(it.label)}</b><span>${esc(fmt(it.value))} · ${pct(it.value / total, 0)}</span>`}</div>`;
    })
    .join('')}</div>`;
}

/**
 * 流向图(桑基):左边一列是钱从哪来,右边一列是到哪去,中间的带子越粗钱越多。
 * nodes 是各大类的名字;links 是(从第几类、到第几类、多少)。每一类左右各一块,高按它流出、流入的合计。
 */
export function sankey(nodes: string[], links: { from: number; to: number; amt: number }[], fmt: (v: number) => string): string {
  const ls = links.filter((l) => l.amt > 0 && l.from !== l.to);
  if (!ls.length) return `<div class="sb-empty">没有</div>`;
  const W = 560, pad = 6, nodeW = 10, labelW = 96;
  const out = nodes.map((_, i) => ls.filter((l) => l.from === i).reduce((s, l) => s + l.amt, 0));
  const inn = nodes.map((_, i) => ls.filter((l) => l.to === i).reduce((s, l) => s + l.amt, 0));
  const total = ls.reduce((s, l) => s + l.amt, 0);
  const left = nodes.map((_, i) => i).filter((i) => out[i] > 0).sort((a, b) => out[b] - out[a]);
  const right = nodes.map((_, i) => i).filter((i) => inn[i] > 0).sort((a, b) => inn[b] - inn[a]);
  const rows = Math.max(left.length, right.length);
  const H = Math.max(160, 40 + rows * 34);
  const scale = (H - pad * (rows + 1)) / total;
  const lx = labelW, rx = W - labelW - nodeW;
  // 每一块的位置
  const ly = new Map<number, number>(), ry = new Map<number, number>();
  let y = pad;
  for (const i of left) {
    ly.set(i, y);
    y += out[i] * scale + pad;
  }
  y = pad;
  for (const i of right) {
    ry.set(i, y);
    y += inn[i] * scale + pad;
  }
  const lo = new Map(ly), ro = new Map(ry); // 每块已经用掉的高度
  let s = `<svg class="sk" viewBox="0 0 ${W} ${H}">`;
  // 带子:大的先画在下面
  for (const l of [...ls].sort((a, b) => b.amt - a.amt)) {
    const h = l.amt * scale;
    const y0 = lo.get(l.from)!, y1 = ro.get(l.to)!;
    lo.set(l.from, y0 + h);
    ro.set(l.to, y1 + h);
    const x0 = lx + nodeW, x1 = rx, mx = (x0 + x1) / 2;
    const c = seriesColor(l.from);
    s += `<path d="M${x0},${y0} C${mx},${y0} ${mx},${y1} ${x1},${y1} L${x1},${y1 + h} C${mx},${y1 + h} ${mx},${y0 + h} ${x0},${y0 + h} Z" style="fill:${c}" opacity="0.32"><title>${esc(`${nodes[l.from]} → ${nodes[l.to]}:${fmt(l.amt)}`)}</title></path>`;
  }
  for (const i of left) {
    const yy = ly.get(i)!, h = Math.max(1, out[i] * scale);
    s += `<rect x="${lx}" y="${yy}" width="${nodeW}" height="${h}" rx="2" style="fill:${seriesColor(i)}"/><text x="${lx - 6}" y="${yy + h / 2 + 4}" text-anchor="end" class="n">${esc(nodes[i])}</text><text x="${lx - 6}" y="${yy + h / 2 + 15}" text-anchor="end" class="v">${esc(fmt(out[i]))}</text>`;
  }
  for (const i of right) {
    const yy = ry.get(i)!, h = Math.max(1, inn[i] * scale);
    s += `<rect x="${rx}" y="${yy}" width="${nodeW}" height="${h}" rx="2" style="fill:${seriesColor(i)}"/><text x="${rx + nodeW + 6}" y="${yy + h / 2 + 4}" class="n">${esc(nodes[i])}</text><text x="${rx + nodeW + 6}" y="${yy + h / 2 + 15}" class="v">${esc(fmt(inn[i]))}</text>`;
  }
  return s + `</svg>`;
}

/** 表格里的数据条:格子里垫一条长短条(frac 是 0 到 1),数照样写在上面。 */
export function dbar(html: string, frac: number, color = 'var(--ink-3)'): string {
  const f = Math.max(0, Math.min(1, Number.isFinite(frac) ? frac : 0));
  return `<span class="db"><i style="width:${(f * 100).toFixed(1)}%;background:${color}"></i><span>${html}</span></span>`;
}
